import React, { useState, useCallback, useMemo, useEffect } from 'react';
import JsBarcode from 'jsbarcode';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Print as PrintIcon,
  QrCode2 as BarcodeIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Inventory as ProductIcon,
  Label as LabelIcon,
  Visibility as PreviewIcon,
  CheckBox as SelectAllIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../utils/currency';
import { useGetProductsQuery } from '../services/products.service';

interface PrintSettings {
  includePrice: boolean;
  includeName: boolean;
  includeBarcode: boolean;
  copies: number;
}

const BarcodePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [printSettings, setPrintSettings] = useState<PrintSettings>({
    includePrice: true,
    includeName: true,
    includeBarcode: true,
    copies: 1,
  });

  // Simple debounce implementation
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: productsData, isLoading: isProductsLoading } = useGetProductsQuery({
    search: debouncedSearchTerm,
    page: page,
    limit: 50, // Much smaller limit with pagination
  });

  // Memoize products for performance
  const products = useMemo(() => {
    return productsData?.products || [];
  }, [productsData?.products]);
  
  // Memoize filtered products for performance
  const filteredProducts = useMemo(() => {
    return products.filter((product: any) => 
      product.barcode && (
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.barcode.includes(searchTerm)
      )
    );
  }, [products, searchTerm]);

  // Generate barcode for 2x1 inch thermal labels (optimized for preview)
  const generateBarcodeDataURL = useCallback((text: string): string => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 192; // 2 inches at 96 DPI
      canvas.height = 96;  // 1 inch at 96 DPI
      
      JsBarcode(canvas, text, {
        format: 'CODE128',
        width: 1.2,
        height: 39, // 30% taller: 30 * 1.3 = 39
        displayValue: false,
        margin: 2,
        background: '#ffffff',
        lineColor: '#000000',
      });
      
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error generating barcode:', error);
      return '';
    }
  }, []);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProducts(filteredProducts.map((p: any) => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleProductSelect = (productId: string, checked: boolean) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    }
  };

  // Print function optimized for 2x1 inch thermal labels
  const handlePrint = () => {
    const selectedProductsData = products.filter((p: any) => selectedProducts.includes(p.id));
    
    if (selectedProductsData.length === 0) {
      alert('Por favor selecciona al menos un producto para imprimir');
      return;
    }

    // Create labels HTML for thermal printer
    let labelsHTML = '';

    selectedProductsData.forEach((product: any) => {
      for (let copy = 0; copy < printSettings.copies; copy++) {
        // Generate barcode SVG
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        JsBarcode(svg, product.barcode || product.sku, {
          format: 'CODE128',
          width: 1.2,
          height: 32.5, // 30% taller: 25 * 1.3 = 32.5
          displayValue: false,
          margin: 0,
          background: 'transparent',
          lineColor: '#000',
        });

        const barcodeHTML = svg.outerHTML;

        labelsHTML += `
          <div class="label">
            ${printSettings.includeName ? `<div class="product-name">${product.name}</div>` : ''}
            ${printSettings.includeBarcode ? `
              <div class="barcode-container">
                ${barcodeHTML}
              </div>
            ` : ''}
            ${printSettings.includePrice ? `<div class="price">${formatCurrency(parseFloat(product.salePrice))}</div>` : ''}
            <div class="distributor">Plasticos Juticalpa</div>
          </div>
        `;
      }
    });

    // Open print window optimized for thermal labels
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Etiquetas Térmicas - 2x1 Pulgadas</title>
            <style>
              @page { 
                size: 2in 1in;
                margin: 0;
              }
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body { 
                font-family: Arial, sans-serif;
                font-size: 8px;
                line-height: 1.1;
              }
              .label {
                width: 2in;
                height: 1in;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: 1mm;
                padding-top: calc(1mm + 20px);
                page-break-after: always;
                background: white;
              }
              .label:last-child {
                page-break-after: auto;
              }
              .product-name {
                font-size: 12px;
                font-weight: bold;
                text-align: center;
                margin-bottom: 1mm;
                line-height: 1.2;
                word-wrap: break-word;
                max-width: 100%;
              }
              .barcode-container {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 1mm 0;
              }
              .barcode-container svg {
                max-width: 100%;
                height: auto;
              }
              .price {
                font-size: 16px; /* Reduced from 20.8px to prevent cutting */
                font-weight: bold;
                text-align: center;
                margin-top: 1mm;
              }
              .distributor {
                font-size: 14px;
                font-weight: bold;
                text-align: center;
                margin-top: 0.5mm;
                color: #666;
              }
            </style>
          </head>
          <body>
            ${labelsHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      
      // Auto-print after loading
      setTimeout(() => {
        printWindow.print();
      }, 100);
    }
  };

  const handlePreview = () => {
    setPreviewDialog(true);
  };

  // Preview function for 2x1 inch thermal labels
  const renderPreviewLabels = () => {
    const selectedProductsData = products.filter((p: any) => selectedProducts.includes(p.id));

    return selectedProductsData.slice(0, 6).map((product: any) => (
      <Card
        key={product.id}
        sx={{
          width: '192px', // 2 inches at 96 DPI
          height: '96px',  // 1 inch at 96 DPI
          border: '1px solid #000',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: '2mm',
          m: 0.5,
          fontSize: '8px',
        }}
      >
        {printSettings.includeName && (
          <Typography 
            variant="caption" 
            fontWeight="bold" 
            textAlign="center" 
            sx={{ fontSize: '18.2px', lineHeight: 1.1, mb: '1mm' }} /* Double size: 9.1 * 2 = 18.2 */
            noWrap
          >
            {product.name}
          </Typography>
        )}
        
        {printSettings.includeBarcode && (product.barcode || product.sku) && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', my: '1mm' }}>
            <img 
              src={generateBarcodeDataURL(product.barcode || product.sku)} 
              alt={`Barcode for ${product.barcode || product.sku}`}
              style={{ 
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '39px' /* 30% taller: 30 * 1.3 = 39 */
              }}
            />
          </Box>
        )}

        {printSettings.includePrice && (
          <Typography 
            variant="caption" 
            fontWeight="bold" 
            textAlign="center"
            sx={{ fontSize: '20.8px', mt: '1mm' }} /* Double size: 10.4 * 2 = 20.8 */
          >
            {formatCurrency(parseFloat(product.salePrice))}
          </Typography>
        )}
        
        {/* Distributor footer - always shown */}
        <Typography 
          variant="caption" 
          textAlign="center"
          sx={{ 
            fontSize: '5px', 
            mt: '0.5mm', 
            color: '#666', 
            fontStyle: 'italic',
            lineHeight: 0.9
          }}
        >
          Distribuido por Plasticos Juticalpa
        </Typography>
      </Card>
    ));
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            🏷️ Impresión de Códigos de Barras
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Selecciona productos y genera etiquetas personalizadas para imprimir
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsDialog(true)}
          >
            Configurar
          </Button>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            disabled={selectedProducts.length === 0}
          >
            Vista Previa
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={selectedProducts.length === 0}
          >
            Imprimir ({selectedProducts.length})
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ProductIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Total Productos</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {products.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SelectAllIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Seleccionados</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {selectedProducts.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LabelIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Etiquetas a Imprimir</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="secondary">
                {selectedProducts.length * printSettings.copies}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BarcodeIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Tamaño Etiqueta</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                2" × 1" TÉRMICA
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Buscar productos por nombre, SKU o código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<SelectAllIcon />}
              onClick={() => handleSelectAll(true)}
            >
              Seleccionar Todos
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => setSelectedProducts([])}
              disabled={selectedProducts.length === 0}
            >
              Limpiar Selección
            </Button>
          </Box>
        </Box>

        {selectedProducts.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{selectedProducts.length}</strong> productos seleccionados. 
              Se imprimirán <strong>{selectedProducts.length * printSettings.copies}</strong> etiquetas térmicas 
              de <strong>2" × 1"</strong>.
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Products Table */}
      <Paper>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                    indeterminate={selectedProducts.length > 0 && selectedProducts.length < filteredProducts.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Precio</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isProductsLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={7}>
                      <Typography>Cargando productos...</Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                filteredProducts.map((product: any) => (
                  <TableRow key={product.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProducts.includes(product.id)}
                        onChange={(e) => handleProductSelect(product.id, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {product.name}
                        </Typography>
                        {product.description && (
                          <Typography variant="caption" color="text.secondary">
                            {product.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {product.sku}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={product.category} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(parseFloat(product.salePrice || 0))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {product.stock} {product.unit}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.sku ? 'Listo' : 'Sin SKU'}
                        size="small"
                        color={product.sku ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {productsData?.pagination && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {((page - 1) * 50) + 1}-{Math.min(page * 50, productsData.pagination.total)} de {productsData.pagination.total} productos
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                Página {page} de {Math.ceil(productsData.pagination.total / 50)}
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                disabled={page >= Math.ceil(productsData.pagination.total / 50)}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Settings Dialog - Thermal Label Configuration */}
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon />
            Configuración - Etiquetas Térmicas 2x1"
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Optimizado para impresoras térmicas de etiquetas de 2 × 1 pulgadas
            </Alert>

            <TextField
              fullWidth
              label="Copias por Producto"
              type="number"
              value={printSettings.copies}
              onChange={(e) => setPrintSettings({ ...printSettings, copies: Math.max(1, parseInt(e.target.value) || 1) })}
              inputProps={{ min: 1, max: 10 }}
              helperText="Número de etiquetas a imprimir por producto"
            />

            <Divider />

            <Typography variant="subtitle2" color="primary">Contenido de la Etiqueta</Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={printSettings.includeName}
                  onChange={(e) => setPrintSettings({ ...printSettings, includeName: e.target.checked })}
                />
              }
              label="Incluir nombre del producto"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={printSettings.includeBarcode}
                  onChange={(e) => setPrintSettings({ ...printSettings, includeBarcode: e.target.checked })}
                />
              }
              label="Incluir código de barras"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={printSettings.includePrice}
                  onChange={(e) => setPrintSettings({ ...printSettings, includePrice: e.target.checked })}
                />
              }
              label="Incluir precio de venta"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => setSettingsDialog(false)}>
            Guardar Configuración
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Vista Previa de Etiquetas
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Mostrando las primeras 6 etiquetas como ejemplo. Se imprimirán {selectedProducts.length * printSettings.copies} etiquetas en total.
          </Alert>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {renderPreviewLabels()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>
            Cerrar
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
            Imprimir Ahora
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BarcodePage;